import Vue from 'vue'

/**
 * 多页签 (tagsView) 状态 — 轻量响应式单例 (Vue.observable), 不进大 Vuex.
 * 页签 = 一个路由 name; keep-alive 按"组件名"缓存, 切页签不刷新, 关页签才销毁.
 * 首页 (粮食入库) 固定不可关、永远第一个.
 */

// 固定首页: 与路由 /entry-v1 对应, 组件名 EntryV1
// [2026-09-08] 菜单名「粮食入库」→「粮食过磅录入」, 首页页签标题同步
const HOME = {
  routeName: 'entry-v1',
  title: '粮食过磅录入',
  path: '/entry-v1',
  compName: 'EntryV1',
}
function makeHome() {
  return { ...HOME, closable: false, _refreshing: false }
}

const state = Vue.observable({
  tabs: [makeHome()],
  // [2026-10-08 性能] 页签最近访问序 (routeName, 新在前) — keep-alive 缓存 LRU 淘汰依据。
  // 此前已打开页签全部缓存无上限, 财务一天开十几个页签每页保留全量 data, 4G 内存机器越用越卡;
  // 超过上限后最久未访问的可关闭页签被移出缓存 (实例销毁, 切回时重新加载), 首页永不淘汰。
  lastUsed: [],
})

// 可缓存的可关闭页签上限 (LRU; 首页 closable:false 不计入、永不淘汰 —
// 入库表单未提交内容与海康 HWND 都活在首页实例里, 淘汰会丢表单/跳窗)
const MAX_CACHED_TABS = 10

// 记录访问序 (导航后 addTab 都会调用, 含已存在页签)
function touch(routeName) {
  const i = state.lastUsed.indexOf(routeName)
  if (i >= 0) state.lastUsed.splice(i, 1)
  state.lastUsed.unshift(routeName)
}

export const tabsStore = {
  get tabs() {
    return state.tabs
  },
  // keep-alive include: 首页(不可关)常驻 + 按 lastUsed 取最近 MAX_CACHED_TABS 个可关闭页签的组件名
  // (去重; 跳过正在刷新的, 让其被销毁后重新挂载)。被 LRU 淘汰的页签若还开着, 切回时组件重建重新拉数据。
  get cachedNames() {
    const out = []
    state.tabs.forEach((t) => {
      if (t._refreshing) return
      if (t.compName && t.closable === false && out.indexOf(t.compName) === -1) out.push(t.compName)
    })
    const tabByRoute = new Map(state.tabs.map((t) => [t.routeName, t]))
    let budget = MAX_CACHED_TABS
    for (const rn of state.lastUsed) {
      const t = tabByRoute.get(rn)
      if (!t || t._refreshing || !t.compName || t.closable === false) continue
      if (out.indexOf(t.compName) === -1) {
        out.push(t.compName)
        budget -= 1
        if (budget <= 0) break
      }
    }
    return out
  },
  // 导航后调用: 没有则新增页签; 已有则更新 path/标题/组件名 (同路由不同 query 复用同一页签)
  addTab(tab) {
    if (!tab || !tab.routeName) return
    touch(tab.routeName)
    const existing = state.tabs.find((t) => t.routeName === tab.routeName)
    if (existing) {
      if (tab.path) existing.path = tab.path
      if (tab.compName) existing.compName = tab.compName
      if (tab.title) existing.title = tab.title
      return
    }
    state.tabs.push({
      routeName: tab.routeName,
      title: tab.title || tab.routeName,
      path: tab.path || '/' + tab.routeName,
      compName: tab.compName || '',
      closable: true,
      _refreshing: false,
    })
  },
  // 关闭一个页签; 返回关闭后应跳转到的页签 (null = 无需跳转 / 关的不是当前页 / 首页不可关)
  closeTab(routeName, currentRouteName) {
    const idx = state.tabs.findIndex((t) => t.routeName === routeName)
    if (idx < 0 || state.tabs[idx].closable === false) return null
    const wasActive = routeName === currentRouteName
    state.tabs.splice(idx, 1)
    const lu = state.lastUsed.indexOf(routeName)
    if (lu >= 0) state.lastUsed.splice(lu, 1)
    if (!wasActive) return null
    return state.tabs[idx - 1] || state.tabs[state.tabs.length - 1] || null
  },
  // 关闭除某页签外的全部 (首页始终保留)
  closeOthers(routeName) {
    state.tabs = state.tabs.filter((t) => t.closable === false || t.routeName === routeName)
    state.lastUsed = state.lastUsed.filter((rn) => state.tabs.some((t) => t.routeName === rn))
  },
  // 关闭全部 (只留首页); 返回首页页签
  closeAll() {
    state.tabs = state.tabs.filter((t) => t.closable === false)
    state.lastUsed = state.lastUsed.filter((rn) => state.tabs.some((t) => t.routeName === rn))
    return state.tabs[0] || null
  },
  // 拖拽排序 (首页钉在最左, 不参与拖动、也不允许别的页插到它前面)
  moveTab(fromIdx, toIdx) {
    const arr = state.tabs
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || fromIdx >= arr.length || toIdx >= arr.length) return
    if (arr[fromIdx].closable === false) return
    if (toIdx === 0) toIdx = 1
    const moving = arr.splice(fromIdx, 1)[0]
    arr.splice(toIdx, 0, moving)
  },
  // 刷新某页签: 临时移出缓存让 keep-alive 销毁旧实例, 双 tick + setTimeout 后恢复 -> 当前激活页重新挂载(拉新数据)
  // 单 nextTick 不够 — Vue 2 keep-alive 销毁/重建需要至少 2 个 tick + 小延时让 DOM 真正脱离
  refresh(vm, routeName) {
    const tab = state.tabs.find((t) => t.routeName === routeName)
    if (!tab) return Promise.resolve()
    tab._refreshing = true
    return new Promise((resolve) => {
      vm.$nextTick(() => {
        setTimeout(() => {
          tab._refreshing = false
          vm.$nextTick(resolve)
        }, 30)
      })
    })
  },
}

export default tabsStore
